// Detalle de una regla sobre el chasis común del editor: campos, nota de clawback e historial.
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import type { RetailCommission } from '@fiado/type-kit';
import { CommissionRuleStatusEnum } from '@fiado/type-kit/bin/retailCommission/enums/CommissionRuleStatusEnum';
import { userMessageOf } from '@/api/errors';
import { Badge, Button, usePermissions } from '@/kit';
import { FieldHelp } from '@/shared/patterns/EditorForm';
import { RULE_HELP } from '../../constants/helpText';
import { formatDateMx } from '@/lib/dateHelpers';
import {
    LIFECYCLE_ACTION_ICON,
    LifecycleActions,
    LifecycleConfirmDialog,
    LifecycleEditorShell,
    LifecycleNotice,
    type LifecycleAction,
    type LifecycleConfirmation,
} from '@/shared/patterns/Lifecycle';
import {
    isRuleConflict,
    useCreateRule,
    useDeleteRule,
    useRuleAction,
    useUpdateRule,
} from '../../api/commissionQueries';
import type { CommissionDirectory } from '../../api/useCommissionDirectory';
import { COMMISSION_PERMISSIONS } from '../../lib/commissionPermissions';
import {
    emptyRuleForm,
    formToInput,
    ruleToForm,
    validateRuleForm,
    type RuleFormErrors,
    type RuleFormValues,
} from '../../lib/ruleForm';
import {
    describeLevers,
    isNpiSensitive,
    menuActionsFor,
    publishStateOf,
    ruleKind,
    savePublishes,
    STATUS_LIFECYCLE,
    type RuleMenuAction,
} from '../../lib/ruleText';
import { RuleFields } from './RuleFields';
import { RuleHistoryList } from './RuleHistoryList';

type Rule = RetailCommission.CommissionRule;
type StatusAction = 'publish' | 'pause' | 'resume' | 'expire';

const { DRAFT, EXPIRED, PAUSED, SCHEDULED } = CommissionRuleStatusEnum;

const ACTION_LABEL: Record<RuleMenuAction, string> = {
    publish: 'Publicar',
    pause: 'Pausar',
    resume: 'Reanudar',
    expire: 'Vencer ahora',
    duplicate: 'Duplicar',
    simulate: 'Correr simulador',
    delete: 'Eliminar',
};

const ACTION_DONE: Record<StatusAction, string> = {
    publish: 'Regla publicada',
    pause: 'Regla pausada',
    resume: 'Regla reanudada',
    expire: 'Regla vencida',
};

const ACTION_PERMISSION: Record<RuleMenuAction, string> = {
    publish: COMMISSION_PERMISSIONS.planPublish,
    pause: COMMISSION_PERMISSIONS.planWrite,
    resume: COMMISSION_PERMISSIONS.planWrite,
    expire: COMMISSION_PERMISSIONS.planWrite,
    duplicate: COMMISSION_PERMISSIONS.planWrite,
    simulate: COMMISSION_PERMISSIONS.planRead,
    delete: COMMISSION_PERMISSIONS.planWrite,
};

const same = (a: RuleFormValues, b: RuleFormValues) => JSON.stringify(a) === JSON.stringify(b);
/** Las acciones no suben la versión pero sí `updatedAt`: las dos marcan un cambio en el motor. */
const revision = (rule: Rule) => `${String(rule.version)}@${rule.updatedAt}`;

interface RuleEditorProps {
    /** `null` es una regla nueva. */
    rule: Rule | null;
    /** Con lo que abre el editor: la regla, una nueva vacía o una copia. */
    initial: RuleFormValues;
    directory: CommissionDirectory;
    policies: RetailCommission.CommissionPolicies | undefined;
    canWrite: boolean;
    onDirtyChange: (dirty: boolean) => void;
    onSaved: (rule: Rule) => void;
    onDeleted: () => void;
    onCancelNew: () => void;
    onDuplicate: (values: RuleFormValues) => void;
    onSimulate: (ruleId: string) => void;
}

export function RuleEditor(props: RuleEditorProps) {
    const {
        rule,
        initial,
        directory,
        policies,
        canWrite,
        onDirtyChange,
        onSaved,
        onDeleted,
        onCancelNew,
        onDuplicate,
        onSimulate,
    } = props;
    // La copia de la regla con la que se abrió el editor: de ella sale `expectedVersion`.
    const [loaded, setLoaded] = useState(rule);
    const [values, setValues] = useState(initial);
    // Una regla nueva se compara contra el formulario vacío: una copia abre con cambios sin guardar.
    const [baseline, setBaseline] = useState(() =>
        rule === null ? emptyRuleForm(initial.effectiveFrom) : initial,
    );
    const [errors, setErrors] = useState<RuleFormErrors>({});
    const [confirming, setConfirming] = useState<'expire' | 'delete' | null>(null);
    const create = useCreateRule();
    const update = useUpdateRule();
    const action = useRuleAction();
    const remove = useDeleteRule();
    const { hasPermission } = usePermissions();
    const canPublish = hasPermission(COMMISSION_PERMISSIONS.planPublish);
    // En una regla que ya aplica, lo que se guarda es lo que se paga.
    const publishOnSave = savePublishes(rule) && canPublish;

    const input = useMemo(() => formToInput(values), [values]);
    const dirty = !same(values, baseline);
    const readOnly = !canWrite || rule?.status === EXPIRED;
    const busy = action.isPending || remove.isPending;

    const adopt = (next: Rule) => {
        const fresh = ruleToForm(next);
        setLoaded(next);
        setValues(fresh);
        setBaseline(fresh);
        setErrors({});
    };

    // Otra versión de la regla llegó del motor: sin cambios entra sola; con cambios se avisa.
    const upstream = rule !== null && loaded !== null && revision(rule) !== revision(loaded);
    if (upstream && !dirty) adopt(rule);
    const stale = upstream && dirty;

    const reload = () => {
        if (!rule) return;
        adopt(rule);
        onDirtyChange(false);
    };

    const change = (patch: Partial<RuleFormValues>) => {
        const next = { ...values, ...patch };
        setValues(next);
        onDirtyChange(!same(next, baseline));
    };

    const discard = () => {
        if (rule === null) {
            onCancelNew();
            return;
        }
        setValues(baseline);
        setErrors({});
        onDirtyChange(false);
    };

    const save = () => {
        if (create.isPending || update.isPending) return;
        const found = validateRuleForm(values);
        setErrors(found);
        const first = Object.values(found).at(0);
        if (first !== undefined) {
            toast.error('Revisa la regla', { description: first });
            return;
        }
        const handlers = {
            onSuccess: ({ rule: saved }: RetailCommission.RuleResponse) => {
                setBaseline(values);
                onDirtyChange(false);
                if (publishOnSave) {
                    publishSaved(saved);
                    return;
                }
                toast.success(rule ? 'Regla guardada' : 'Regla creada', {
                    description:
                        saved.status === DRAFT
                            ? 'Queda en borrador hasta publicarla.'
                            : 'Lo que se paga no cambia hasta volver a publicarla.',
                });
                onSaved(saved);
            },
            onError: (error: unknown) => {
                toast.error(userMessageOf(error));
                // La regla cambió en el motor: se descarta lo editado y el editor abre la versión nueva.
                if (rule && isRuleConflict(error)) discard();
            },
        };
        if (rule) {
            // La versión que se cargó, no la última que llegó: si cambió, el motor responde 409.
            const expectedVersion = (loaded ?? rule).version;
            update.mutate({ ruleId: rule.ruleId, body: { ...input, expectedVersion } }, handlers);
        } else create.mutate(input, handlers);
    };

    /** Publica la versión recién guardada; si falla, queda guardada y el aviso ofrece publicarla. */
    const publishSaved = (saved: Rule) => {
        action.mutate(
            { ruleId: saved.ruleId, action: 'publish', expectedVersion: saved.version },
            {
                onSuccess: ({ rule: next }) => {
                    toast.success('Regla guardada y publicada', {
                        description: 'El motor ya aplica esta versión.',
                    });
                    onSaved(next);
                },
                onError: (error) => {
                    toast.error('Se guardó, pero no se pudo publicar', {
                        description: userMessageOf(error),
                    });
                    onSaved(saved);
                },
            },
        );
    };

    const run = (kind: StatusAction) => {
        if (!rule || action.isPending) return;
        action.mutate(
            { ruleId: rule.ruleId, action: kind, expectedVersion: rule.version },
            {
                onSuccess: ({ rule: next }) => {
                    toast.success(ACTION_DONE[kind]);
                    setConfirming(null);
                    onSaved(next);
                },
                onError: (error) => {
                    toast.error(userMessageOf(error));
                    setConfirming(null);
                },
            },
        );
    };

    const confirmDelete = () => {
        if (!rule || remove.isPending) return;
        remove.mutate(rule.ruleId, {
            onSuccess: () => {
                toast.success('Borrador eliminado');
                setConfirming(null);
                onDeleted();
            },
            onError: (error) => {
                toast.error(userMessageOf(error));
                setConfirming(null);
            },
        });
    };

    const actions: LifecycleAction[] = rule
        ? menuActionsFor(rule).map((key) => ({
              key,
              label: ACTION_LABEL[key],
              primary: key === 'publish',
              permission: ACTION_PERMISSION[key],
              destructive: key === 'expire' || key === 'delete',
              icon: LIFECYCLE_ACTION_ICON[key],
              // Duplicar no toca la versión guardada; lo demás no se repite mientras corre.
              availableWhileDirty: key === 'duplicate',
              disabled: busy && key !== 'duplicate' && key !== 'simulate',
              isLoading: key === 'publish' && action.isPending,
              onSelect: () => {
                  if (key === 'duplicate')
                      onDuplicate({ ...values, name: `${values.name} (copia)` });
                  else if (key === 'simulate') onSimulate(rule.ruleId);
                  else if (key === 'expire' || key === 'delete') setConfirming(key);
                  else run(key);
              },
          }))
        : [];

    const publishState = rule ? publishStateOf(loaded ?? rule) : null;
    const clawback = policies?.clawback ?? null;
    const sensitive = isNpiSensitive(input);

    const untitled = values.name.trim() === '';

    return (
        <LifecycleEditorShell
            ariaLabel={rule ? `Regla ${rule.name}` : 'Regla nueva'}
            title={untitled ? 'Regla sin nombre' : values.name}
            untitled={untitled}
            dirty={rule !== null && dirty}
            subtitle={
                rule
                    ? `Versión ${String((loaded ?? rule).version)} · creada ${formatDateMx(rule.createdAt)}`
                    : 'Nueva · se guarda como borrador'
            }
            status={rule ? STATUS_LIFECYCLE[rule.status] : null}
            id={rule ? { label: 'ID', value: rule.ruleId } : undefined}
            tags={<Badge variant="outline">{ruleKind(input.levers)}</Badge>}
            actions={
                <LifecycleActions
                    actions={actions}
                    dirty={dirty}
                    menuLabel="Acciones de la regla"
                />
            }
            notices={
                <>
                    {rule && publishState && publishState.kind !== 'live' && !dirty && (
                        <LifecycleNotice
                            tone={publishState.kind === 'pending' ? 'warning' : 'muted'}
                        >
                            {publishState.kind === 'pending' ? (
                                <>
                                    <b>Cambios sin publicar.</b> El motor sigue aplicando la v
                                    {publishState.appliedVersion}.
                                    {rule.status === PAUSED &&
                                        ' Al publicarla, la regla vuelve a aplicar.'}
                                </>
                            ) : (
                                <>
                                    <b>Borrador.</b> Todavía no aplica a ninguna venta.
                                </>
                            )}
                            {!canPublish && ' Publicarla necesita el permiso de publicar.'}
                        </LifecycleNotice>
                    )}
                    {stale && (
                        <LifecycleNotice
                            tone="warning"
                            action={
                                <Button size="sm" variant="outline" onClick={reload}>
                                    Recargar
                                </Button>
                            }
                        >
                            Esta regla cambió en el motor. Al recargar se pierden tus cambios sin
                            guardar.
                        </LifecycleNotice>
                    )}
                </>
            }
            history={rule ? <RuleHistoryList ruleId={rule.ruleId} /> : null}
            saveBar={{
                count: dirty && !readOnly ? 1 : 0,
                message: rule ? 'Cambios sin guardar' : 'Regla nueva sin guardar',
                saveLabel: publishOnSave ? 'Guardar y publicar' : 'Guardar cambios',
                saving: create.isPending || update.isPending || action.isPending,
                onDiscard: discard,
                onSave: save,
            }}
        >
            <fieldset disabled={readOnly} className="contents">
                <RuleFields
                    values={values}
                    errors={errors}
                    onChange={change}
                    directory={directory}
                    summary={describeLevers(
                        input.levers,
                        input.countBy,
                        input.payTiming,
                        input.onSaleAdvanceBps,
                    )}
                    rules={
                        <p className="rounded-md bg-muted/60 px-3 py-2 text-[11px]">
                            <span className="inline-flex items-center gap-1 align-middle">
                                <b>Clawback:</b>
                                <FieldHelp label="Clawback" help={RULE_HELP.clawback} />
                            </span>{' '}
                            {clawback
                                ? `aplica política global · cancelación ${String(clawback.cancelacionDias)}d · devolución ${String(clawback.devolucionDias)}d`
                                : 'sin política guardada'}
                            {sensitive ? (
                                <b className="text-warning">
                                    {' '}
                                    · NPI M30{clawback
                                        ? ` ${String(clawback.npiM30Dias)}d`
                                        : ''}{' '}
                                    (esta regla es sensible)
                                </b>
                            ) : (
                                <span> · esta regla no es sensible a NPI M30</span>
                            )}
                        </p>
                    }
                />
            </fieldset>

            <LifecycleConfirmDialog
                confirmation={rule && confirming ? confirmationFor(rule, confirming) : null}
                loading={action.isPending || remove.isPending}
                onCancel={() => {
                    setConfirming(null);
                }}
                onConfirm={() => {
                    if (confirming === 'expire') run('expire');
                    else confirmDelete();
                }}
            />
        </LifecycleEditorShell>
    );
}

/** La confirmación de vencer o eliminar una regla. */
function confirmationFor(rule: Rule, kind: 'expire' | 'delete'): LifecycleConfirmation {
    if (kind === 'delete') {
        return {
            title: `¿Eliminar «${rule.name}»?`,
            description: 'Se borra el borrador con sus versiones. No se puede recuperar.',
            confirmLabel: 'Eliminar',
            destructive: true,
        };
    }
    return {
        title: `¿Vencer «${rule.name}» ahora?`,
        description:
            rule.status === SCHEDULED
                ? 'Todavía no empieza: queda vencida y no llega a aplicar.'
                : 'Sale del plan vigente desde hoy y no vuelve a aplicar.',
        confirmLabel: 'Vencer ahora',
        destructive: true,
    };
}
